import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  GridFilterInputValueProps,
  GridFilterOperator,
  GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import dvdlocator from "../../../../apis/dvdlocator";
import { renderDate } from "../../../../formatting/dateFormat";
import {
  DriftCell,
  InventoryGridLink,
  ProvenanceCell,
  ProvenanceDialog,
  ProvenanceFrame,
  inventoryGridUrlForInventoryIds,
  quantity,
  useProvenanceDetail,
} from "./MetricProvenance";

/**
 * Master Inventory: ATS, MAI and MONO rolled up into one cell. The three are
 * computed from the same rows, the inventory rows linked to each part of the
 * product:
 *   ATS   available to ship: the part with the least on-hand inventory
 *   MAI   master analyst inventory: on hand + on order, least part
 *   MONO  master on order: the part with the least on order
 * Clicking opens the shared detail: every inventory row per part with its
 * quantities at calculation time and now, the limiting part marked, a link
 * to the rows in the Inventory grid and a "Recalculate" action. No freshness
 * dot: this is our own inventory, not supplier data. The member columns stay
 * available (hidden by default) for sorting and filtering on one metric.
 */

export const MASTER_INVENTORY_DESCRIPTION =
  "Master inventory: ATS = available to ship (the part with the least on hand), MAI = master analyst inventory (on hand + on order, least part), MONO = master on order (least part), each over the inventory rows linked to the product's parts. Click for the rows behind the values.";

export type InventoryMetric = "ats" | "mai" | "mono";

export const INVENTORY_METRIC_LABELS: Record<InventoryMetric, string> = {
  ats: "ATS: available to ship",
  mai: "MAI: master analyst inventory (on hand + on order)",
  mono: "MONO: master on order",
};

export interface MasterInventoryValues {
  ats: number | null;
  mai: number | null;
  mono: number | null;
  atsUpdatedAt: string | number | null;
  maiUpdatedAt: string | number | null;
  monoUpdatedAt: string | number | null;
}

export function getMasterInventoryValues(row: any): MasterInventoryValues {
  const p = row?.product ?? {};
  return {
    ats: p.ats ?? null,
    mai: p.master_analyst_inv ?? null,
    mono: p.master_quantity_on_order ?? null,
    atsUpdatedAt: p.ats_updated_at ?? null,
    maiUpdatedAt: p.mai_updated_at ?? null,
    monoUpdatedAt: p.mono_updated_at ?? null,
  };
}

const METRICS: InventoryMetric[] = ["ats", "mai", "mono"];

export function formatMasterInventoryTooltip(v: MasterInventoryValues): string {
  const updated = v.atsUpdatedAt ?? v.maiUpdatedAt ?? v.monoUpdatedAt;
  return [
    ...METRICS.map((m) => `${INVENTORY_METRIC_LABELS[m]}: ${quantity(v[m])}`),
    `Calculated ${updated ? renderDate(updated) : "never"}.`,
    "Click for the inventory rows behind the values.",
  ].join("\n");
}

export const renderMasterInventoryCell = (params: GridRenderCellParams) => {
  const v = getMasterInventoryValues(params.row);
  if (v.ats === null && v.mai === null && v.mono === null) return null;
  return (
    <ProvenanceCell
      tooltip={formatMasterInventoryTooltip(v)}
      maxWidth={820}
      renderDetail={(close) => (
        <InventoryMetricsDetail
          productId={params.row.product?.product_id}
          productTitle={params.row.product?.product_title}
          onClose={close}
        />
      )}
    >
      {METRICS.map((m) => (
        <span key={m}>
          <span style={{ color: "rgba(0,0,0,0.55)", fontSize: "0.8em" }}>
            {m.toUpperCase()}{" "}
          </span>
          {quantity(v[m])}
        </span>
      ))}
    </ProvenanceCell>
  );
};

// ─── Detail ──────────────────────────────────────────────────────────────────

export interface InventoryMetricRow {
  inventory_id: number;
  inventory_title: string | null;
  inv_then: number | null;
  inv_now: number | null;
  ai_then: number | null;
  ai_now: number | null;
  ono_then: number | null;
  ono_now: number | null;
}

export interface InventoryMetricPart {
  part_number: string;
  inv_total: number | null;
  ai_total: number | null;
  ono_total: number | null;
  limits: InventoryMetric[];
  rows: InventoryMetricRow[];
}

export interface InventoryMetricsDetailData {
  product_id: number;
  calculated_at: string | null;
  ats: { value: number | null; updated_at: string | null };
  mai: { value: number | null; updated_at: string | null };
  mono: { value: number | null; updated_at: string | null };
  parts: InventoryMetricPart[];
}

export async function fetchInventoryMetricsDetail(
  productId: number
): Promise<InventoryMetricsDetailData> {
  const response = await dvdlocator.get(
    `/product/${productId}/metrics/inventory`
  );
  return response.data;
}

function Summary({
  data,
  highlight,
}: {
  data: InventoryMetricsDetailData;
  highlight?: InventoryMetric;
}) {
  return (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1 }}>
      {METRICS.map((m) => (
        <Typography
          key={m}
          variant="body2"
          sx={{ fontWeight: m === highlight ? 700 : 400 }}
        >
          {INVENTORY_METRIC_LABELS[m].split(":")[0]} {quantity(data[m].value)}
          {data.parts.some((p) => p.limits.includes(m))
            ? ` (limited by part ${data.parts
                .filter((p) => p.limits.includes(m))
                .map((p) => p.part_number)
                .join(", ")})`
            : ""}
        </Typography>
      ))}
    </Box>
  );
}

export function InventoryMetricsDetail({
  productId,
  productTitle,
  highlight,
  onClose,
}: {
  productId: number;
  productTitle?: string;
  highlight?: InventoryMetric;
  onClose?: () => void;
}) {
  const { detail, error } = useProvenanceDetail(
    productId,
    fetchInventoryMetricsDetail,
    "inventory metrics detail"
  );
  const inventoryIds = detail
    ? Array.from(
        new Set(detail.parts.flatMap((p) => p.rows.map((r) => r.inventory_id)))
      )
    : [];
  return (
    <ProvenanceFrame
      title="Inventory metrics (ATS / MAI / MONO)"
      productTitle={productTitle}
      productId={productId}
      error={error}
      loaded={detail !== null}
      recalcLabel="Recalculate"
      onClose={onClose}
    >
      {detail && (
        <>
          <Summary data={detail} highlight={highlight} />
          <Typography variant="caption" color="text.secondary">
            {detail.parts.length === 0
              ? "No inventory rows were recorded for this product's parts."
              : `Each part is the sum of its inventory rows; each metric is the smallest part. Calculated ${
                  detail.calculated_at
                    ? renderDate(detail.calculated_at)
                    : "never"
                }. "Now" shows the row's current value when it changed since.`}
          </Typography>
          {detail.parts.length > 0 && (
            <Table size="small" sx={{ mt: 0.5 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Part</TableCell>
                  <TableCell>Inventory row</TableCell>
                  <TableCell align="right">Inv then</TableCell>
                  <TableCell align="right">Now</TableCell>
                  <TableCell align="right">On hand + on order then</TableCell>
                  <TableCell align="right">Now</TableCell>
                  <TableCell align="right">On order then</TableCell>
                  <TableCell align="right">Now</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detail.parts.flatMap((part) => [
                  ...part.rows.map((row, i) => (
                    <TableRow key={`${part.part_number}-${row.inventory_id}`}>
                      <TableCell>{i === 0 ? part.part_number : ""}</TableCell>
                      <TableCell title={row.inventory_title ?? undefined}>
                        #{row.inventory_id}
                        {row.inventory_title
                          ? ` ${row.inventory_title.slice(0, 40)}${
                              row.inventory_title.length > 40 ? "…" : ""
                            }`
                          : ""}
                      </TableCell>
                      <TableCell align="right">
                        {quantity(row.inv_then)}
                      </TableCell>
                      <TableCell align="right">
                        <DriftCell then={row.inv_then} now={row.inv_now} />
                      </TableCell>
                      <TableCell align="right">
                        {quantity(row.ai_then)}
                      </TableCell>
                      <TableCell align="right">
                        <DriftCell then={row.ai_then} now={row.ai_now} />
                      </TableCell>
                      <TableCell align="right">
                        {quantity(row.ono_then)}
                      </TableCell>
                      <TableCell align="right">
                        <DriftCell then={row.ono_then} now={row.ono_now} />
                      </TableCell>
                    </TableRow>
                  )),
                  <TableRow
                    key={`${part.part_number}-total`}
                    sx={{ "& td": { fontWeight: 700, borderBottomWidth: 2 } }}
                  >
                    <TableCell>{part.part_number} total</TableCell>
                    <TableCell>
                      {part.limits.length > 0
                        ? `limits ${part.limits
                            .map((m) => m.toUpperCase())
                            .join(", ")}`
                        : ""}
                    </TableCell>
                    <TableCell align="right">
                      {quantity(part.inv_total)}
                    </TableCell>
                    <TableCell />
                    <TableCell align="right">
                      {quantity(part.ai_total)}
                    </TableCell>
                    <TableCell />
                    <TableCell align="right">
                      {quantity(part.ono_total)}
                    </TableCell>
                    <TableCell />
                  </TableRow>,
                ])}
              </TableBody>
            </Table>
          )}
          <InventoryGridLink
            href={inventoryGridUrlForInventoryIds(inventoryIds)}
            count={inventoryIds.length}
            noun="inventory row"
          />
        </>
      )}
    </ProvenanceFrame>
  );
}

export function InventoryMetricsDetailDialog({
  open,
  onClose,
  product,
}: {
  open: boolean;
  onClose: () => void;
  product: any;
}) {
  return (
    <ProvenanceDialog
      open={open}
      onClose={onClose}
      title="Inventory metrics (ATS / MAI / MONO)"
    >
      <InventoryMetricsDetail
        productId={product?.product_id}
        productTitle={product?.product_title}
      />
    </ProvenanceDialog>
  );
}

// ─── Filtering ────────────────────────────────────────────────────────────────

export interface MasterInventoryFilterValue {
  ats?: { operator: string; value?: string | number };
  mai?: { operator: string; value?: string | number };
  mono?: { operator: string; value?: string | number };
  atsUpdatedAt?: {
    operator: "within" | "olderThan" | "never" | "ever";
    days?: number;
  };
}

const NUMBER_OPERATORS = [
  { value: ">", label: ">" },
  { value: ">=", label: "≥" },
  { value: "<", label: "<" },
  { value: "<=", label: "≤" },
  { value: "=", label: "=" },
  { value: "!=", label: "≠" },
  { value: "isEmpty", label: "is empty" },
  { value: "isNotEmpty", label: "is not empty" },
];
const VALUELESS = new Set(["isEmpty", "isNotEmpty"]);

export function isMasterInventoryFilterComplete(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const v = value as MasterInventoryFilterValue;
  const numberOk = (c?: { operator: string; value?: unknown }) =>
    !!c?.operator &&
    (VALUELESS.has(c.operator) || (c.value !== undefined && c.value !== ""));
  const dateOk = (c?: { operator: string; days?: number }) =>
    !!c?.operator &&
    (c.operator === "never" ||
      c.operator === "ever" ||
      Number.isFinite(Number(c.days)));
  return (
    numberOk(v.ats) ||
    numberOk(v.mai) ||
    numberOk(v.mono) ||
    dateOk(v.atsUpdatedAt)
  );
}

function NumberCondition({
  label,
  condition,
  onChange,
}: {
  label: string;
  condition?: { operator: string; value?: string | number };
  onChange: (c?: { operator: string; value?: string | number }) => void;
}) {
  const operator = condition?.operator ?? "";
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          value={operator}
          onChange={(e) => {
            const op = e.target.value;
            onChange(
              op ? { operator: op, value: condition?.value } : undefined
            );
          }}
        >
          <MenuItem value="">any</MenuItem>
          {NUMBER_OPERATORS.map((op) => (
            <MenuItem key={op.value} value={op.value}>
              {op.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {operator && !VALUELESS.has(operator) && (
        <TextField
          size="small"
          type="number"
          value={condition?.value ?? ""}
          onChange={(e) =>
            onChange({
              operator,
              value: e.target.value === "" ? undefined : e.target.value,
            })
          }
          sx={{ width: 110 }}
        />
      )}
    </Box>
  );
}

function DateCondition({
  label,
  condition,
  onChange,
}: {
  label: string;
  condition?: MasterInventoryFilterValue["atsUpdatedAt"];
  onChange: (c?: MasterInventoryFilterValue["atsUpdatedAt"]) => void;
}) {
  const op = condition?.operator ?? "";
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          value={op}
          onChange={(e) => {
            const next = e.target.value as typeof op;
            onChange(
              next
                ? { operator: next as any, days: condition?.days ?? 7 }
                : undefined
            );
          }}
        >
          <MenuItem value="">any time</MenuItem>
          <MenuItem value="within">within last</MenuItem>
          <MenuItem value="olderThan">older than</MenuItem>
          <MenuItem value="ever">calculated</MenuItem>
          <MenuItem value="never">never calculated</MenuItem>
        </Select>
      </FormControl>
      {(op === "within" || op === "olderThan") && (
        <TextField
          size="small"
          type="number"
          label="days"
          value={condition?.days ?? ""}
          onChange={(e) =>
            onChange({
              operator: op,
              days: e.target.value === "" ? undefined : Number(e.target.value),
            })
          }
          slotProps={{ htmlInput: { min: 0 } }}
          sx={{ width: 90 }}
        />
      )}
    </Box>
  );
}

export function MasterInventoryFilterFields({
  value,
  onChange,
}: {
  value: MasterInventoryFilterValue | undefined;
  onChange: (value: MasterInventoryFilterValue) => void;
}) {
  const v: MasterInventoryFilterValue = value ?? {};
  const set = (patch: Partial<MasterInventoryFilterValue>) =>
    onChange({ ...v, ...patch });
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        py: 0.5,
        minWidth: 280,
      }}
    >
      <NumberCondition
        label="ATS"
        condition={v.ats}
        onChange={(c) => set({ ats: c })}
      />
      <NumberCondition
        label="MAI"
        condition={v.mai}
        onChange={(c) => set({ mai: c })}
      />
      <NumberCondition
        label="MONO"
        condition={v.mono}
        onChange={(c) => set({ mono: c })}
      />
      <DateCondition
        label="Calculated"
        condition={v.atsUpdatedAt}
        onChange={(c) => set({ atsUpdatedAt: c })}
      />
      <Typography variant="caption" color="text.secondary">
        ATS = available to ship; MAI = on hand + on order; MONO = on order.
      </Typography>
    </Box>
  );
}

function MasterInventoryFilterInput({
  item,
  applyValue,
}: GridFilterInputValueProps) {
  const [draft, setDraft] = useState<MasterInventoryFilterValue>(
    item.value ?? {}
  );
  useEffect(() => {
    setDraft(item.value ?? {});
  }, [item.value]);
  return (
    <MasterInventoryFilterFields
      value={draft}
      onChange={(next) => {
        setDraft(next);
        applyValue({ ...item, value: next });
      }}
    />
  );
}

export function getMasterInventoryFilterOperators(): GridFilterOperator[] {
  return [
    {
      label: "matches",
      value: "rollup",
      getApplyFilterFn: () => null,
      InputComponent: MasterInventoryFilterInput,
    },
  ];
}
